import { cloneElement, useEffect } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useFlag } from '@unleash/proxy-client-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { FeatureToggle } from 'utils/unleash/toggles';

import useComplaintDevice from 'hooks/useComplaintDevice';
import {
  getMessageTemplates,
  getPartnerPreferences,
  postPartnerPreferences,
} from 'services/suggestedMessages';

import { Loading, NotificationBar } from 'components';
import Menu from 'components/Menu';
import SuggestedMessagesSetup from 'modals/SuggestedMessagesSetup';

import { PageContainerProps } from './types';

import * as S from './styles';

const PageContainer: React.FC<PageContainerProps> = ({
  children,
  isAuthenticatedSpecialist,
}) => {
  const [searchParams] = useSearchParams();
  const suggestedMessagesEnabled = useFlag(FeatureToggle.SuggestedMessagesV1);
  const { pathname } = useLocation();
  const { handleSetComplaintDevice } = useComplaintDevice();

  const { data: partnerPreferences, ...partnerPreferencesRequest } = useQuery({
    queryFn: () => getPartnerPreferences('first_booking'),
    queryKey: ['partnerPreferences', 'first_booking'],
  });

  const { data: suggestedMessagesTemplates, ...messageTemplatesRequest } =
    useQuery({
      queryFn: () => getMessageTemplates('first_booking'),
      queryKey: ['messageTemplates'],
      enabled:
        partnerPreferencesRequest.status === 'success' &&
        partnerPreferences?.length === 0,
    });

  const { mutate: sendPartnerPreferences } = useMutation({
    mutationFn: ({ messageTemplateId }: { messageTemplateId: number }) =>
      postPartnerPreferences(messageTemplateId),
    onSuccess: () => {
      partnerPreferencesRequest.refetch();
    },
  });

  const hideMenu = Boolean(searchParams.get('hideMenu'));
  const showMenu = isAuthenticatedSpecialist && !hideMenu;

  const messageTemplatesLoaded =
    Array.isArray(suggestedMessagesTemplates) &&
    suggestedMessagesTemplates.length > 0;

  const showSuggestedMessagesModal =
    suggestedMessagesEnabled &&
    partnerPreferences?.length === 0 &&
    messageTemplatesLoaded;

  useEffect(() => {
    handleSetComplaintDevice(false);
  }, [pathname]);

  return (
    <>
      <Loading
        isLoading={
          partnerPreferencesRequest.isFetching ||
          messageTemplatesRequest.isFetching
        }
      />
      {showMenu && <Menu />}
      <S.Container showMenu={showMenu}>
        {cloneElement(children, { isAuthenticatedSpecialist })}
        <NotificationBar />
      </S.Container>
      {messageTemplatesLoaded && (
        <SuggestedMessagesSetup
          open={showSuggestedMessagesModal}
          messageTemplates={suggestedMessagesTemplates}
          onSubmit={(messageTemplateId: number) => {
            sendPartnerPreferences({ messageTemplateId });
          }}
        />
      )}
    </>
  );
};
export default PageContainer;
